import { eventChannel } from 'redux-saga'
import { take, put, call, select } from 'redux-saga/effects'
import { auth, functions } from '../../../utilities/firebase'
import validateEmail from '../../../utilities/validateEmail'
import getAbsoluteUrlForCurrentHost from '../../../utilities/getAbsoluteUrlForCurrentHost'
import getUserProfile from '../../../dataservices/getUserProfile'
import setUserInfo from '../../../dataservices/setUserInfo'
import verifyUserLocalStorage from '../../../dataservices/verifyUserLocalStorage'
import addAppErrorLogEntry from '../../../dataservices/addAppErrorLogEntry'
import addVerificationToUserByUserName from '../../../dataservices/addVerificationToUserByUserName'
import messageTypes from '../../../enums/messageTypes'
import { localisedError, customError, logError } from '../../../utilities/Errors'
import that from '../../../utilities/translationHelper'
import actionCreators from '../../actionCreators' // eslint-disable-line import/no-named-as-default
import { selectUserProfile } from '../../selectors'
import { getCustomClaims, facebookLoginWithPopup, facebookLoginWithRedirect, sendLoginLinkToEmail, isLoginWithEmailLink, loginWithEmailLink, getRedirectResult, logout as authLogout, instagramLoginWithRedirect, instagramLoginWithRedirectComplete, instagramLoginWithPopup } from '../firebase/auth'

const EMAIL_FOR_LOGIN = 'emailForLogin'

const safe = func => (...args) => func(...args).catch(error => ({ error }))

// this one is really only exported for testing
export function* authenticateUser(authUser) {
	let user = { isAnonymous: true }
	const uid = authUser.uid
	if (uid) {
		const { isAdmin, isActivated } = yield call(getCustomClaims)
		user = { uid, isAdmin, isActivated }

		const { userProfile } = yield call(safe(getUserProfile), { uid, withFlags: false })
		if (userProfile) {
			user.userProfile = userProfile
		}

		// TODO: remove this after refactoring code using this
		global.localStorage.setItem('uid', uid)
	} else {
		global.localStorage.removeItem('uid')
	}
	yield put(actionCreators.auth.complete.trigger({ user }))
}

export function* authStateChangedWatch() {
	const authChannel = eventChannel((emitter) => {
		auth.onAuthStateChanged((user) => {
			emitter(user || {})
		})
		return () => { }
	})
	while (true) {
		const user = yield take(authChannel)
		try {
			yield call(authenticateUser, user)
		} catch (error) {
			logError('SAGA ERROR: authStateChangedWatch', error)
			addAppErrorLogEntry('AUTHENTICATION_WATCH', user, { error, sagaStack: error.sagaStack, stack: error.stack }, 'AUTH')
		}
	}
}

export function* onLogoutSuccess({ silent }) {
	if (!silent) {
		yield put(actionCreators.appMessageInternal(messageTypes.POSITIVE, that.context.translate('You have been successfully logged out')))
	}
}

export function* onLoginSuccess() {
	yield put(actionCreators.appMessageInternal(messageTypes.POSITIVE, that.context.translate('You have been successfully logged in')))
}

export function* facebookLogin() {
	let userInfo
	try {
		userInfo = yield call(facebookLoginWithPopup)
	} catch (error) {
		if (error && error.code === 'auth/account-exists-with-different-credential') {
			// TODO: Account linking. For now, just error and ask them to login again.
			throw localisedError(that.context.translate('An account already exists with that email address. Please login using email.'))
		} else {
			throw error
		}
	}
	return { userInfo }
}

export function* instagramLogin() {
	const userInfo = yield call(instagramLoginWithPopup)
	// TODO: Email validation and account linking. For now, this cannot fail with auth/account-exists-with-different-credential
	return { userInfo }
}

export function* emailLogin({ email }) {
	if (!validateEmail(email)) {
		throw localisedError(that.context.translate('Invalid email address'))
	} else {
		//	Generate email login url
		const redirectUrl = getAbsoluteUrlForCurrentHost('/completeEmailLogin')

		const actionCodeSettings = {
			url: redirectUrl,
			// This must be true. Otherwise Google throw: 'handleCodeInApp must be true when sending login link to email', great work Google...
			// Argh. Solution: https://stackoverflow.com/questions/46211693/firebase-passing-statecontinue-to-website-link-in-password-reset-email-and-ema
			// You MUST setup the iOS or Android app linking facility...
			// Me to Google: 'Do you even authenticate, bro?'
			handleCodeInApp: true,
			iOS: {
				//	TODO: Set correct ios bundle id
				bundleId: 'com.example.ios'
			},
			android: {
				//	TODO: Set correct android package name
				packageName: 'com.example.android',
				installApp: true,
				minimumVersion: '12'
			}
		}

		yield put(actionCreators.authEmail.sendEmail.trigger({ email, url: redirectUrl }))
		yield call(sendLoginLinkToEmail, email, actionCodeSettings)
		// Save the email locally so you don't need to ask the user for it again
		global.localStorage.setItem(EMAIL_FOR_LOGIN, email)

		return { email }
	}
}

export function* emailLoginComplete({ email: providedEmail }) {
	// Confirm the link is a login with email link.
	const isLinkValid = yield call(isLoginWithEmailLink, global.location.href)
	if (isLinkValid) {
		if (providedEmail) {
			// Save the email locally so you don't need to ask the user for it again
			global.localStorage.setItem(EMAIL_FOR_LOGIN, providedEmail)
		}
		const email = providedEmail || global.localStorage.getItem(EMAIL_FOR_LOGIN)
		if (!email) {
			yield put(actionCreators.authEmail.emailRequired.trigger())
			throw customError('Asking for email')
		} else {
			// check for existing email login
			const userProfile = yield select(selectUserProfile)
			if (userProfile && userProfile.email === email) {
				yield put(actionCreators.authEmail.loginAlreadyComplete.trigger())
				throw customError('Login already complete')
			}
			const userInfo = yield call(loginWithEmailLink, email, global.location.href)
			// TODO: Note: this cannot fail with auth/account-exists-with-different-credential (email just takes precedence) - the next login with the other auth provider would ask for linking
			return { userInfo }
		}
	} else {
		throw customError('Tried to launch login email from incorrect URL')
	}
}

export function* facebookRedirectLogin() {
	global.history.replaceState({}, 'completeFacebookRedirectLogin', '/completeFacebookRedirectLogin')
	yield call(facebookLoginWithRedirect)
}

export function* facebookRedirectLoginComplete() {
	const userInfo = yield call(getRedirectResult)
	if (!userInfo.user) {
		throw customError('Unknown facebookRedirectLoginComplete error')
	}
	return { userInfo }
}

export function* instagramRedirectLogin() {
	yield call(instagramLoginWithRedirect)
}

export function* instagramRedirectLoginComplete() {
	const userInfo = yield call(instagramLoginWithRedirectComplete)
	return { userInfo }
}

export function* createUserProfile({ userInfo }) {
	// create profile for new users
	let userProfile
	let shouldCreate = userInfo && userInfo.additionalUserInfo && userInfo.additionalUserInfo.isNewUser
	if (!shouldCreate) {
		// make sure it is in the state (it might be if AUTH_COMPLETE has beaten us)
		userProfile = yield select(selectUserProfile)
		// or that we can get it from the firestore (this is most likely, login saga usually runs before the auth event happens)
		if (!userProfile && userInfo && userInfo.user && userInfo.user.uid) {
			const result = yield call(safe(getUserProfile), { uid: userInfo.user.uid, withFlags: false })
			userProfile = result.userProfile
		}
		// otherwise, profile should be there but is not, so create it (this is so that we generate the profile just in case it failed or was lost somehow)
		shouldCreate = !userProfile
	}
	if (shouldCreate) {
		const { data } = yield call(functions.httpsCallable('createUserProfile'), { additionalUserInfo: userInfo.additionalUserInfo })
		userProfile = data
	}
	if (userProfile) {
		yield put(actionCreators.userProfileSuggest({ userProfile }))
	}
}

export function* createVerifications() {
	const verifyUserObject = verifyUserLocalStorage.getVerifyUserObject()
	if (verifyUserObject) {
		const verifiedUserObject = yield call(addVerificationToUserByUserName, verifyUserObject.userName)
		verifyUserLocalStorage.removeVerifyUserObject()
		yield put(actionCreators.verifyUserReset())
		if (verifiedUserObject.success) {
			yield put(actionCreators.verifyUserSuccess(verifiedUserObject))
		} else {
			yield put(actionCreators.verifyUserFail(verifiedUserObject))
		}
	}
}

export function* login({ userInfo }) {
	// this will throw if the user is not activated, that is okay
	yield call(setUserInfo, userInfo)
}

export function* logout() {
	yield call(authLogout)
}

export function* cleanupData() {
	// Clear email from storage.
	yield global.localStorage.removeItem(EMAIL_FOR_LOGIN)
}



// WEBPACK FOOTER //
// ./src/redux/saga/auth/sagas.js